import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Card from '../templates/ui/Card'
import Button from '../templates/ui/Button'
import CenterPopup from '../templates/ui/CenterPopup'
import CenterToast from '../templates/ui/CenterToast'
import UserAvatar from '../templates/ui/UserAvatar'
import { useCampaignStore } from '../app/store/campaignStore'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { muteluApi } from '../services/muteluApi'
import { usePartyStatus } from '../hooks/usePartyStatus'
import shareHelper from '../utils/shareHelper'
import { buildMuteluPartyJoinShareUrl } from '../utils/muteluLiffUrl'
import { buildApiErrorPopupTemplate } from '../templates/popupTemplate'
import { logPageApiCall, logPageApiError, logPageApiResult } from '../utils/apiDebugLogger'
import lineOfficialIcon from '../../../assets/images/game-mutelu/line-app-logo.png'
import goldenCrownIcon from '../../../assets/images/game-mutelu/golden-crown.png'
import './MultiplyWealth.css'

const getGroupCode = (payload) =>
  payload?.group_code ||
  payload?.group?.group_code ||
  payload?.data?.group_code ||
  payload?.data?.group?.group_code ||
  null

const normalizeDisplayName = (value) => {
  const normalized = String(value || '').trim()
  if (!normalized) return ''
  if (normalized === 'ผู้เล่น') return ''
  return normalized
}
const getDisplayName = (member, fallback) =>
  normalizeDisplayName(member?.display_name) || normalizeDisplayName(member?.name) || fallback
const getPictureUrl = (member) => member?.picture_url || member?.pictureUrl || ''
const normalizeMember = (member) => ({
  ...member,
  is_owner: Boolean(member?.is_owner || member?.isOwner || member?.owner || member?.role === 'owner')
})
const extractMembers = (payload) => {
  const candidates = [payload?.data?.members, payload?.members, payload?.data]
  const list = candidates.find((entry) => Array.isArray(entry))
  return Array.isArray(list) ? list.map(normalizeMember) : []
}
const normalizeMessage = (value, fallback = '') => {
  if (typeof value === 'string') return value
  if (value == null) return fallback
  if (typeof value === 'object') {
    if (typeof value.message === 'string') return value.message
    try {
      return JSON.stringify(value)
    } catch {
      return fallback
    }
  }
  return String(value)
}
const CONNECTION_ERROR_MESSAGE = 'ไม่สามารถเชื่อมต่อกับระบบ กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ต'
const isConnectionError = (message) => /ไม่สามารถเชื่อมต่อ|ตรวจสอบอินเทอร์เน็ต|network|timeout/i.test(String(message || ''))
const PARTY_COMPLETE_POPUP_KEY_PREFIX = 'mutelu:party-complete-popup'

export default function MultiplyWealth() {
  const { groupCode: groupCodeFromPath } = useParams()
  const navigate = useNavigate()
  const { state } = useCampaignStore()
  const [groupCode, setGroupCode] = useState(groupCodeFromPath || '')
  const [isBooting, setIsBooting] = useState(true)
  const [isCreatingGroup, setIsCreatingGroup] = useState(false)
  const [centerToast, setCenterToast] = useState('')
  const [groupMembers, setGroupMembers] = useState([])
  const [apiPopupTemplate, setApiPopupTemplate] = useState(null)
  const [rewardPopupTemplate, setRewardPopupTemplate] = useState(null)
  const lastApiErrorRef = useRef('')
  const groupRecoverTriedRef = useRef(false)
  const centerToastTimerRef = useRef(null)
  const localProfile = useMemo(
    () => ({
      displayName: localStorage.getItem('line_display_name') || '',
      pictureUrl: localStorage.getItem('line_picture_url') || ''
    }),
    []
  )
  const { groupData, error: groupError, loading: groupStatusLoading } = usePartyStatus(groupCode, false)

  const openApiErrorPopup = useCallback((title, subtitle, message) => {
    const normalized = normalizeMessage(message, 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง')
    if (!normalized) return
    if (isConnectionError(normalized)) {
      setCenterToast(CONNECTION_ERROR_MESSAGE)
      if (centerToastTimerRef.current) {
        window.clearTimeout(centerToastTimerRef.current)
      }
      centerToastTimerRef.current = window.setTimeout(() => setCenterToast(''), 1700)
      return
    }
    if (lastApiErrorRef.current === normalized) return
    lastApiErrorRef.current = normalized
    setApiPopupTemplate(
      buildApiErrorPopupTemplate({
        title,
        subtitle,
        message: normalized
      })
    )
  }, [])

  useEffect(() => {
    let mounted = true

    const bootstrap = async () => {
      setIsBooting(true)
      try {
        if (groupCodeFromPath) {
          if (mounted) setGroupCode(groupCodeFromPath)
          return
        }

        muteluApi.initPlayerOnce().then(() => {
          logPageApiResult('MultiplyWealth', 'initPlayer', { success: true })
        }).catch((error) => {
          logPageApiError('MultiplyWealth', 'initPlayer', error)
        })

        logPageApiCall('MultiplyWealth', 'getMyOwnedParty')
        const ownedGroupResponse = await muteluApi.getMyOwnedParty()
        logPageApiResult('MultiplyWealth', 'getMyOwnedParty', ownedGroupResponse)
        const ownedCode = getGroupCode(ownedGroupResponse)
        if (mounted && ownedCode) {
          setGroupCode(ownedCode)
          return
        }

        // No owned party yet: create immediately when user enters /party flow.
        logPageApiCall('MultiplyWealth', 'createParty (auto on enter)')
        const createdGroupResponse = await muteluApi.createParty()
        logPageApiResult('MultiplyWealth', 'createParty (auto on enter)', createdGroupResponse)
        const createdCode = getGroupCode(createdGroupResponse)
        if (mounted && createdCode) {
          setGroupCode(createdCode)
        }
      } catch (error) {
        logPageApiError('MultiplyWealth', 'bootstrap', error)
        if (mounted) {
          openApiErrorPopup('โหลดปาร์ตี้ไม่สำเร็จ', error?.data?.message || error?.message || 'ไม่สามารถโหลดข้อมูลกลุ่มได้')
        }
      } finally {
        if (mounted) setIsBooting(false)
      }
    }

    bootstrap()
    return () => {
      mounted = false
    }
  }, [groupCodeFromPath, openApiErrorPopup])

  const ensureGroupCode = useCallback(async () => {
    if (groupCode) return groupCode

    setIsCreatingGroup(true)
    try {
      logPageApiCall('MultiplyWealth', 'createParty')
      const createResponse = await muteluApi.createParty()
      logPageApiResult('MultiplyWealth', 'createParty', createResponse)
      const newCode = getGroupCode(createResponse)
      if (!newCode) {
        throw new Error('สร้างกลุ่มไม่สำเร็จ')
      }
      setGroupCode(newCode)
      return newCode
    } catch (error) {
      logPageApiError('MultiplyWealth', 'createParty', error)
      openApiErrorPopup('สร้างปาร์ตี้ไม่สำเร็จ', 'หน้า Party', error?.data?.message || error?.message || 'ไม่สามารถสร้างปาร์ตี้ได้')
      throw error
    } finally {
      setIsCreatingGroup(false)
    }
  }, [groupCode, openApiErrorPopup])

  useEffect(() => {
    return () => {
      if (centerToastTimerRef.current) {
        window.clearTimeout(centerToastTimerRef.current)
      }
    }
  }, [])

  const showCenterToast = useCallback((message) => {
    setCenterToast(message)
    if (centerToastTimerRef.current) {
      window.clearTimeout(centerToastTimerRef.current)
    }
    centerToastTimerRef.current = window.setTimeout(() => {
      setCenterToast('')
    }, 1700)
  }, [])

  useEffect(() => {
    let mounted = true

    const fetchMembers = async () => {
      if (!groupCode) {
        if (mounted) setGroupMembers([])
        return
      }

      const statusMembers = Array.isArray(groupData?.members) ? groupData.members.map(normalizeMember) : []
      if (statusMembers.length > 0) {
        if (mounted) setGroupMembers(statusMembers)
        return
      }

      const normalizedGroupError = String(groupError || '')
      if (/GROUP_NOT_FOUND|ไม่พบปาร์ตี้/i.test(normalizedGroupError)) {
        if (mounted) setGroupMembers([])
        return
      }

      if (groupStatusLoading) return

      try {
        logPageApiCall('MultiplyWealth', 'getPartyMembers', { groupCode })
        const response = await muteluApi.getPartyMembers(groupCode)
        logPageApiResult('MultiplyWealth', 'getPartyMembers', response)
        if (!mounted) return
        setGroupMembers(extractMembers(response))
      } catch (error) {
        logPageApiError('MultiplyWealth', 'getPartyMembers', error)
        if (mounted) {
          setGroupMembers([])
          openApiErrorPopup('โหลดสมาชิกไม่สำเร็จ', 'หน้า Party', error?.data?.message || error?.message || 'ไม่สามารถโหลดสมาชิกปาร์ตี้ได้')
        }
      }
    }

    fetchMembers()
    return () => {
      mounted = false
    }
  }, [groupCode, groupData?.members, groupError, groupStatusLoading, openApiErrorPopup])

  useEffect(() => {
    if (!groupCodeFromPath || !groupError) return
    if (groupRecoverTriedRef.current) return

    const normalizedGroupError = String(groupError || '')
    if (!/GROUP_NOT_FOUND|ไม่พบปาร์ตี้/i.test(normalizedGroupError)) return

    groupRecoverTriedRef.current = true
    ;(async () => {
      try {
        const ownedGroupResponse = await muteluApi.getMyOwnedParty()
        const ownedCode = getGroupCode(ownedGroupResponse)
        if (ownedCode) {
          setGroupCode(ownedCode)
          navigate(`/game/mutelu/party/${ownedCode}`, { replace: true })
          return
        }
        navigate('/game/mutelu/party', { replace: true })
      } catch {
        navigate('/game/mutelu/party', { replace: true })
      }
    })()
  }, [groupCodeFromPath, groupError, navigate])

  const members = useMemo(() => {
    if (groupMembers.length > 0) return groupMembers
    return Array.isArray(groupData?.members) ? groupData.members.map(normalizeMember) : []
  }, [groupMembers, groupData?.members])
  const ownerMember = useMemo(() => members.find((member) => member?.is_owner) || null, [members])
  const guestMembers = useMemo(() => members.filter((member) => !member?.is_owner).slice(0, 3), [members])
  const ownerPicture = getPictureUrl(ownerMember) || localProfile.pictureUrl
  const ownerName = getDisplayName(ownerMember, localProfile.displayName || '')
  const membersCount =
    Number(groupData?.members_count || 0) ||
    groupMembers.length ||
    members.length ||
    (groupCode ? 1 : 0)
  const hasPartyData = Boolean(groupData?.group_code || groupCode || membersCount > 0)

  useEffect(() => {
    if (!groupError) return
    if (hasPartyData) {
      logPageApiError('MultiplyWealth', 'group status fallback with existing party data', { message: groupError })
      return
    }
    openApiErrorPopup('โหลดสถานะปาร์ตี้ไม่สำเร็จ', 'หน้า Party', groupError)
  }, [groupError, hasPartyData, openApiErrorPopup])

  useEffect(() => {
    if (!groupData?.group_code) return
    if (groupData?.status !== 'completed') return
    if (!groupData?.gold_rewarded) return

    const rewardDate = groupData?.completed_at || groupData?.created_at || 'unknown-date'
    const rewardAmount = Number(groupData?.completion_reward?.amount || 3)
    const popupKey = `${PARTY_COMPLETE_POPUP_KEY_PREFIX}:${groupData.group_code}:${rewardDate}`
    if (localStorage.getItem(popupKey) === '1') return
    localStorage.setItem(popupKey, '1')

    setRewardPopupTemplate({
      icon: '🎉',
      title: `ได้รับตั๋วทอง คนละ ${rewardAmount} ใบ`,
      subtitle: '',
      message: '',
      confirmLabel: 'ตกลง',
      cancelLabel: ''
    })
  }, [groupData])

  const onShareLine = async () => {
    try {
      const ensuredCode = await ensureGroupCode()
      const ensuredShareUrl = buildMuteluPartyJoinShareUrl(ensuredCode)
      const result = await shareHelper.smartShare(ensuredCode, ensuredShareUrl)
      if (result?.success === false) {
        showCenterToast('share link แชร์ไม่สำเร็จ กรุณาลองอีกครั้ง')
        return
      }
      showCenterToast('กำลังเปิด LINE เพื่อแชร์')
    } catch {
      showCenterToast('share link แชร์ไม่สำเร็จ กรุณาลองอีกครั้ง')
    }
  }

  const onCopyLink = async () => {
    try {
      const ensuredCode = await ensureGroupCode()
      const ensuredShareUrl = buildMuteluPartyJoinShareUrl(ensuredCode)
      const result = await shareHelper.copyToClipboard(ensuredShareUrl)
      if (result?.success) {
        showCenterToast('คัดลอกลิงก์แล้ว')
        return
      }
      showCenterToast('คัดลอกลิงก์ไม่สำเร็จ')
    } catch {
      showCenterToast('คัดลอกลิงก์ไม่สำเร็จ')
    }
  }

  return (
    <section className="mobile-page party-page mx-auto w-full max-w-md">
      <Card className="mobile-panel wealth-card">
        <div className="landing-header px-4 py-4 md:px-6">
          <p className="landing-badge">Big C Receipt Mutelu</p>
          <h1 className="landing-title mt-1 md:text-3xl">{state?.campaign?.party?.campaignName || state?.campaign?.campaignName || 'ตั้งตี้คูณทรัพย์'}</h1>
          <p className="landing-subtitle mt-1">{state?.campaign?.party?.tagline || state?.campaign?.tagline || 'รวมพลังมูเตลู'}</p>
        </div>

        {/* <div className="wealth-head">
          <p className="wealth-badge">ชวนเพื่อนอีก 3 คน</p>
          <h2 className="wealth-title">ตั้งตี้คูณทรัพย์</h2>
          <p className="wealth-sub">รวมพลังมูด้วยตี้คนสนิท เพื่อปลดล็อคโบนัสโชคในขั้นถัดไป</p>
        </div> */}

        <div className="party-orbit">
          <div className="party-ring" aria-hidden="true" />
          <div className="party-member party-owner">
            <div className="party-avatar party-avatar-owner">
              <UserAvatar
                src={ownerPicture}
                alt={ownerName}
                fallback="👤"
                imageClassName="party-avatar-img"
                priority
              />
            </div>
            <span className="party-label">{ownerName}</span>
          </div>
          {[0, 1, 2].map((index) => {
            const member = guestMembers[index]
            const name = getDisplayName(member, '')
            const pictureUrl = getPictureUrl(member)
            return (
              <div key={`guest-${index + 1}`} className={`party-member party-guest party-guest-${index + 1}`}>
                <div className={`party-avatar ${member ? '' : 'party-avatar-empty'}`}>
                  {member ? (
                    <>
                      <UserAvatar
                        src={pictureUrl}
                        alt={name}
                        fallback="🙂"
                        imageClassName="party-avatar-img"
                        priority
                      />
                      {Boolean(member?.is_new_member) ? (
                        <img
                          src={goldenCrownIcon}
                          alt=""
                          className="party-new-member-crown"
                          aria-hidden="true"
                        />
                      ) : null}
                    </>
                  ) : '+'}
                </div>
                <span className="party-label">{name}</span>
              </div>
            )
          })}
        </div>

        <div className="party-info">
          <p className="party-title">ชวนเพื่อน 3 คนเข้า'ตี้' ({membersCount}/4)</p>
          <p className="party-sub">
            เงื่อนไขสำคัญ : ในตี้ต้องมี 'เพื่อนใหม่' (New User)
          </p>
          {/* {groupCode ? (
            <p className="party-sub">โค้ดตี้: {groupCode}</p>
          ) : null} */}
          {isBooting || isCreatingGroup ? <p className="party-sub">กำลังเตรียมข้อมูลตี้...</p> : null}
          {/* {groupError && !isConnectionError(groupError) && !hasPartyData ? (
            <p className="party-sub party-sub-error">{normalizeMessage(groupError, 'โหลดสถานะตี้ไม่สำเร็จ')}</p>
          ) : null} */}
        </div>

        <div className="party-page-actions">
          <div className="party-share-actions">
            <Button className="btn-uniform wealth-btn party-btn share-line-primary w-full" onClick={onShareLine} disabled={isCreatingGroup}>
              <span className="party-btn-icon party-btn-icon-line" aria-hidden="true">
                <img className="line-share-icon-image" src={lineOfficialIcon} alt="" />
              </span>
              <span>Share LINE</span>
            </Button>
            <Button className="btn-uniform wealth-btn party-btn share-copy-btn w-full" onClick={onCopyLink} disabled={isCreatingGroup}>
              <span>Copy link</span>
            </Button>
          </div>
          <Link to="/game/mutelu/activity" className="party-activity-link">
            <Button className="btn-uniform wealth-btn party-btn party-activity-btn w-full">
              ไปหน้า Activity
            </Button>
          </Link>
        </div>
      </Card>

      <CenterPopup
        open={Boolean(apiPopupTemplate)}
        template={apiPopupTemplate}
        onCancel={() => {
          setApiPopupTemplate(null)
          lastApiErrorRef.current = ''
        }}
        onConfirm={() => {
          setApiPopupTemplate(null)
          lastApiErrorRef.current = ''
        }}
      />
      <CenterPopup
        open={Boolean(rewardPopupTemplate)}
        template={rewardPopupTemplate}
        onCancel={() => setRewardPopupTemplate(null)}
        onConfirm={() => setRewardPopupTemplate(null)}
      />
      <CenterToast open={Boolean(centerToast)} message={centerToast} />
    </section>
  )
}
